<template>
  <div class="h-full w-full relative">
    <EditorDeckSlidesEditorOverlay :show-content="showVideoReplace">
      <template #top-bar>
        <div class="flex justify-between items-center">
          <div class="max-w-md">
            <p v-if="slide.file" class="mb-0">
              Source name:
              <VLink class="text-copper-300" component="a" font-weight="normal" :href="slide.file && slide.file.url" target="_blank">
                {{ slide.file.name }}
              </VLink>
            </p>
          </div>

          <VButton
            v-if="!showVideoReplace && isInternalAdmin"
            ref="replaceVideoButtonRef"
            class="flex items-center"
            size="xs"
            @click="$emit('update:showVideoReplace', true)"
          >
            <ArrowRotate class="w-3 mr-2" />
            Replace Video
          </VButton>
          <button
            v-if="showVideoReplace"
            class="close-btn appearance-none p-0 border-0 bg-transparent transition-opacity hover_opacity-80"
            :style="{ paddingTop: '5px', paddingBottom: '5px' }"
            type="button"
            @click="$emit('update:showVideoReplace', false)"
          >
            <XIcon />
          </button>
        </div>
      </template>

      <template #content>
        <VideoSlide :slide="slide" />
      </template>

      <template #overlay>
        <div class="grid place-items-center h-full">
          <EditorLocalOrServerChoice
            ref="localOrServerChoiceContainerRef"
            showing
            :class="{ 'pointer-events-none': !showVideoReplace }"
            :company-id="companyId"
            :folder-id="folderId"
            :max-resolution="[4096, 4096]"
            file-type="video"
            mime-type="video/mp4"
            @choose-file="chooseFiles($event)"
            @goto:shelf="showShelf = true"
            @error="$emit('error', $event)"
          />
        </div>
      </template>
    </EditorDeckSlidesEditorOverlay>

    <EditorDeckModalsShelfSelector
      type="videos"
      :folder-id="folderId"
      :company-id="companyId"
      :showing="showShelf"
      action="update"
      file-type="video"
      mime-type="video/mp4"
      @choose-file="chooseFiles($event)"
      @close="showShelf = false"
    >
      <template #footer><span /></template>
    </EditorDeckModalsShelfSelector>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { ref, onUnmounted, computed } from 'vue'

import type { Slide } from '@/entities/deck'
import type { ShelfFile } from '@/entities/shelfFile'
import XIcon from '@/components/icons/x.svg'
import ArrowRotate from '@/components/icons/ArrowRotate.svg'

const props = defineProps({
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    required: true,
  },
  folderId: {
    type: Number,
    default: null,
  },
  companyId: {
    type: [Number, Object] as PropType<number | null>,
    required: true,
  },
  showVideoReplace: {
    type: Boolean,
    required: true,
  },
  isInternalAdmin: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:showVideoReplace', 'update:slide', 'error'])

const showShelf = ref(false)
const selectedFile = ref<ShelfFile | null>(null)
const replaceVideoButtonRef = ref<any>(null)
const localOrServerChoiceContainerRef = ref<any>(null)

useClickOutside(
  [computed(() => replaceVideoButtonRef.value?.$el), computed(() => localOrServerChoiceContainerRef.value?.$el)],
  showShelf,
  close
)

onUnmounted(() => {
  close()
})

function chooseFiles(file: ShelfFile) {
  emit('update:slide', {
    id: props.slide.id,
    file_id: file.id,
  })

  close()
}

function close() {
  showShelf.value = false
  selectedFile.value = null
  emit('update:showVideoReplace', false)
}
</script>
